import {InvoicePrintAccent, InvoicePrintLayoutSettings} from './_models'

// The print template's layout as both print pages and the print-template designer read it. The
// ranges and defaults are the backend's (Services/PrintTemplates/PrintTemplateLayoutRules.cs).

export const LOGO_HEIGHT_RANGE = {min: 10, max: 40}
export const SIGNATURE_HEIGHT_RANGE = {min: 14, max: 45}

/**
 * The smallest a stamp is printed when the sheet is nearly full. Below it the text of a stamp can no
 * longer be read on paper; the stamp then goes to the next sheet instead of shrinking further.
 */
export const LEGIBLE_SIGNATURE_MM = 16

export const DEFAULT_PRINT_LAYOUT: InvoicePrintLayoutSettings = {
  logoHeightMm: 21,
  signatureHeightMm: 24,
  printSignatureByDefault: true,
  showBuyerSignatureBox: true,
  orientation: 'auto',
  accent: 'navy',
  configured: false,
}

/** The colour of each accent: headings, frames and the totals band of the A4 page. */
export const ACCENT_COLORS: Record<InvoicePrintAccent, {label: string; ink: string; soft: string}> =
  {
    navy: {label: 'سرمه‌ای', ink: '#013460', soft: '#e8eef6'},
    black: {label: 'سیاه (مناسب چاپ سیاه‌وسفید)', ink: '#1f1f1f', soft: '#efefef'},
    green: {label: 'سبز', ink: '#0f5e4b', soft: '#e6f2ee'},
    maroon: {label: 'زرشکی', ink: '#7a1f2b', soft: '#f5e9eb'},
  }

const clamp = (value: unknown, range: {min: number; max: number}, fallback: number) => {
  const number = typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : fallback
  return Math.min(range.max, Math.max(range.min, number))
}

/** The layout an invoice prints with; the defaults for a backend or template without one. */
export const printLayoutOf = (
  source?: {layout?: Partial<InvoicePrintLayoutSettings> | null} | null
): InvoicePrintLayoutSettings => {
  const layout = source?.layout || {}
  return {
    logoHeightMm: clamp(layout.logoHeightMm, LOGO_HEIGHT_RANGE, DEFAULT_PRINT_LAYOUT.logoHeightMm),
    signatureHeightMm: clamp(
      layout.signatureHeightMm,
      SIGNATURE_HEIGHT_RANGE,
      DEFAULT_PRINT_LAYOUT.signatureHeightMm
    ),
    printSignatureByDefault: layout.printSignatureByDefault ?? true,
    showBuyerSignatureBox: layout.showBuyerSignatureBox ?? true,
    orientation:
      layout.orientation === 'portrait' || layout.orientation === 'landscape'
        ? layout.orientation
        : 'auto',
    accent:
      layout.accent && layout.accent in ACCENT_COLORS
        ? (layout.accent as InvoicePrintAccent)
        : DEFAULT_PRINT_LAYOUT.accent,
    configured: Boolean(layout.configured),
  }
}
